/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Ticket, ArrowRight, Sparkles } from "lucide-react";
import { billetterieApi } from "@/lib/api";
import Header from "@/components/Header";
import { useSeo } from "@/lib/seo";
import Footer from "@/components/Footer";

function fmtDateLong(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function BilletterieList() {
  useSeo({
    title: "Billetterie & Événements | My Animation",
    description: "Découvrez les prochains événements My Animation — soirées, blind tests, spectacles. Achat de billets en ligne 100% sécurisé.",
    path: "/billetterie",
    keywords: "billetterie DJ, blind test Nord, événement Hauts-de-France, soirée Bousbecque",
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    billetterieApi.publicList().then(setEvents).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main data-testid="billetterie-list">
        {/* Hero coloré */}
        <section className="relative overflow-hidden pt-32 pb-20 px-6">
          <div className="absolute inset-0 opacity-40" style={{ background: "linear-gradient(135deg, #FF3D71 0%, #FF8C42 50%, #7C3AED 100%)" }} />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.1),transparent)]" />
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-pink-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />

          <div className="relative max-w-5xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.35em] text-[#dcb457] mb-4 inline-flex items-center gap-2">
              <Sparkles size={14} /> Festivals & Soirées
            </p>
            <h1 className="font-serif-luxe text-5xl md:text-7xl leading-[1.05] drop-shadow-lg">
              La <span className="italic" style={{ background: "linear-gradient(135deg, #FFD700, #FF3D71)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>billetterie</span>
              <br />officielle.
            </h1>
            <p className="text-white/85 text-base md:text-lg mt-6 max-w-2xl mx-auto">
              Réservez vos places en quelques secondes. Paiement sécurisé · Billet par email · Pas de file d'attente.
            </p>
          </div>
        </section>

        {/* Liste */}
        <section className="px-6 py-16 max-w-6xl mx-auto">
          {loading ? <p className="text-white/55 italic">Chargement…</p>
            : events.length === 0 ? (
              <div className="text-center py-20">
                <Ticket size={48} strokeWidth={1} className="text-[#DCB457] mx-auto mb-4" />
                <p className="text-white/65 text-lg">Aucun événement pour le moment.</p>
                <p className="text-white/45 text-sm mt-2">Revenez bientôt — nos prochains festivals arrivent !</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map((ev) => (
                  <Link key={ev.id} to={`/billetterie/${ev.slug}`} data-testid={`event-${ev.slug}`} className="group block">
                    <article className="relative overflow-hidden h-72 rounded-lg shadow-2xl transition-transform hover:scale-[1.02]">
                      {!ev.hero_no_overlay && <div className="absolute inset-0" style={{ background: ev.bg_gradient || `linear-gradient(135deg, ${ev.primary_color}, ${ev.secondary_color})` }} />}
                      {ev.hero_no_overlay && <div className="absolute inset-0 bg-[#0a0a0a]" />}
                      {ev.hero_image_url && <img src={ev.hero_image_url} alt={ev.name} className={`absolute inset-0 w-full h-full object-cover transition-opacity ${ev.hero_no_overlay ? "opacity-100 group-hover:opacity-95" : "opacity-60 group-hover:opacity-75"}`} />}
                      <div className={`absolute inset-0 bg-gradient-to-t ${ev.hero_no_overlay ? "from-black/90 via-black/20 to-transparent" : "from-black/85 via-black/30 to-transparent"}`} />
                      <div className="relative h-full flex flex-col justify-end p-6">
                        {ev.tagline && <p className="text-[#dcb457] text-xs uppercase tracking-[0.25em] mb-2">{ev.tagline}</p>}
                        <h2 className="text-white font-serif-luxe text-3xl drop-shadow-lg">{ev.name}</h2>
                        <p className="text-white/85 text-sm mt-3 inline-flex items-center gap-1.5"><Calendar size={12} /> {fmtDateLong(ev.date)}</p>
                        {(ev.venue_name || ev.city) && <p className="text-white/75 text-xs mt-1 inline-flex items-center gap-1.5"><MapPin size={11} /> {ev.venue_name || ev.city}</p>}
                        <span className="mt-4 inline-flex items-center gap-1.5 text-white/95 text-xs uppercase tracking-[0.18em] group-hover:gap-3 transition-all">
                          Acheter les billets <ArrowRight size={12} />
                        </span>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
